import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import { formatDZ, formatDZRange } from '../../../utils/currency';

const CategoryFilter = ({ filters, onFilterChange, onClearFilters }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [priceRange, setPriceRange] = useState([0, 5000]);

  const categories = [
    { id: 'phones', name: 'Smartphones', count: 245 },
    { id: 'laptops', name: 'Laptops', count: 189 },
    { id: 'chargers', name: 'Chargers', count: 156 },
    { id: 'accessories', name: 'Accessories', count: 324 },
    { id: 'airpods', name: 'AirPods & Earbuds', count: 98 }
  ];

  const brands = [
    { id: 'apple', name: 'Apple', count: 156 },
    { id: 'samsung', name: 'Samsung', count: 134 },
    { id: 'dell', name: 'Dell', count: 89 },
    { id: 'hp', name: 'HP', count: 76 },
    { id: 'lenovo', name: 'Lenovo', count: 65 },
    { id: 'sony', name: 'Sony', count: 54 },
    { id: 'anker', name: 'Anker', count: 43 }
  ];

  const ratings = [
    { id: '5', name: '5 Stars', count: 234 },
    { id: '4', name: '4 Stars & Up', count: 456 },
    { id: '3', name: '3 Stars & Up', count: 678 },
    { id: '2', name: '2 Stars & Up', count: 789 }
  ];

  const availability = [
    { id: 'in-stock', name: 'In Stock', count: 892 },
    { id: 'pre-order', name: 'Pre-Order', count: 45 },
    { id: 'coming-soon', name: 'Coming Soon', count: 23 }
  ];

  const handlePriceChange = (index, value) => {
    const newRange = [...priceRange];
    newRange[index] = parseInt(value);
    setPriceRange(newRange);
    onFilterChange('priceRange', newRange);
  };

  const handleFilterToggle = (type, value) => {
    const currentFilters = filters?.[type] || [];
    const newFilters = currentFilters?.includes(value)
      ? currentFilters?.filter(item => item !== value)
      : [...currentFilters, value];
    onFilterChange(type, newFilters);
  };

  const FilterSection = ({ title, items, filterType, icon }) => (
    <div className="border-b border-border pb-6 mb-6 last:border-b-0 last:pb-0 last:mb-0">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-foreground flex items-center space-x-2">
          <Icon name={icon} size={18} className="text-primary" />
          <span>{title}</span>
        </h3>
      </div>
      <div className="space-y-3">
        {items?.map((item) => (
          <div key={item?.id} className="flex items-center justify-between">
            <Checkbox
              label={
                <div className="flex items-center justify-between w-full">
                  <span className="text-sm text-foreground">{item?.name}</span>
                  <span className="text-xs text-muted-foreground ml-2">({item?.count})</span>
                </div>
              }
              checked={filters?.[filterType]?.includes(item?.id) || false}
              onChange={() => handleFilterToggle(filterType, item?.id)}
              className="w-full"
            />
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="bg-card border border-border rounded-lg p-6 h-fit">
      {/* Filter Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <Icon name="Filter" size={20} className="text-primary" />
          <h2 className="text-lg font-semibold text-foreground">Filters</h2>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
            iconPosition="right"
          >
            {isExpanded ? 'Collapse' : 'Expand'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onClearFilters}
            iconName="X"
            iconPosition="left"
          >
            Clear All
          </Button>
        </div>
      </div>
      {isExpanded && (
        <div className="space-y-6">
          {/* Price Range */}
          <div className="border-b border-border pb-6 mb-6">
            <div className="flex items-center space-x-2 mb-4">
              <Icon name="DollarSign" size={18} className="text-primary" />
              <h3 className="font-semibold text-foreground">Price Range (DZ)</h3>
            </div>
            <div className="space-y-4">
              <div className="flex items-center space-x-4">
                <div className="flex-1">
                  <label className="text-xs text-muted-foreground mb-1 block">Min Price</label>
                  <input
                    type="range"
                    min="0"
                    max="5000"
                    value={priceRange?.[0]}
                    onChange={(e) => handlePriceChange(0, e?.target?.value)}
                    className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer slider"
                  />
                  <div className="text-sm text-foreground mt-1">{formatDZ(priceRange?.[0])}</div>
                </div>
                <div className="flex-1">
                  <label className="text-xs text-muted-foreground mb-1 block">Max Price</label>
                  <input
                    type="range"
                    min="0"
                    max="5000"
                    value={priceRange?.[1]}
                    onChange={(e) => handlePriceChange(1, e?.target?.value)}
                    className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer slider"
                  />
                  <div className="text-sm text-foreground mt-1">{formatDZ(priceRange?.[1])}</div>
                </div>
              </div>
              <div className="bg-muted/30 rounded-lg p-3">
                <div className="text-sm text-center text-foreground">
                  {formatDZRange(priceRange?.[0], priceRange?.[1])}
                </div>
              </div>
            </div>
          </div>

          {/* Categories */}
          <FilterSection
            title="Categories"
            items={categories}
            filterType="categories"
            icon="Grid3X3"
          />

          {/* Brands */}
          <FilterSection
            title="Brands"
            items={brands}
            filterType="brands"
            icon="Tag"
          />

          {/* Ratings */}
          <FilterSection
            title="Customer Ratings"
            items={ratings}
            filterType="ratings"
            icon="Star"
          />

          {/* Availability */}
          <FilterSection
            title="Availability"
            items={availability}
            filterType="availability"
            icon="Package"
          />

          {/* Apply Filters Button */}
          <div className="pt-4 border-t border-border">
            <Button
              variant="default"
              fullWidth
              iconName="Check"
              iconPosition="left"
              className="trust-seal"
            >
              Apply Filters
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryFilter;